// ============================================================================
// FSA SIGNATURE WORKFLOW TYPES
// ============================================================================
// Mirrors prima.Core.DTOs.FsaModule.Signature.* on the API side.
// See projects/prima/prima-doc/FSA-Signature-Workflow.md for the data model.

import type { ID } from '../index';

// Stronger string ID for grid compatibility (SyutGrid requires `id: string`).
type Uuid = string;

// ---------------------------------------------------------------------------
// Audit Firm
// ---------------------------------------------------------------------------

export interface AuditFirm {
  id: Uuid;
  firmName: string;
  firmRegistrationNumber: string;
  reportAttestationText?: string | null;
  signatureBlockText?: string | null;
  address?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  logoPath?: string | null;
  /** Audit-report page-header mode. */
  letterheadMode: 'TEXT' | 'LOGO_TEXT' | 'BLANK_SPACE';
  /** Firm logo as a base64 data URI (PNG/JPG, cap ~300 KB). */
  logoImageBase64?: string | null;
  /** ADR-0024 A-2 — printed in the auditor-document letterhead footer. */
  gstin?: string | null;
  /** ADR-0024 A-2 — optional firm colour #RRGGBB for the letterhead ("Firm colour" house style). */
  accentColor?: string | null;
  partnerCount: number;
  statusId: ID;
  statusCode: string;
  statusName: string;
  statusColor?: string | null;
  rowVersion: number;
  createdAt: string;
  createdBy?: ID | null;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedBy?: ID | null;
  updatedByName?: string | null;
}

export interface CreateAuditFirmRequest {
  firmName: string;
  firmRegistrationNumber: string;
  reportAttestationText?: string | null;
  signatureBlockText?: string | null;
  address?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  logoPath?: string | null;
  letterheadMode?: 'TEXT' | 'LOGO_TEXT' | 'BLANK_SPACE';
  logoImageBase64?: string | null;
  gstin?: string | null;
  accentColor?: string | null;
  statusCode?: string;
}

export interface UpdateAuditFirmRequest extends CreateAuditFirmRequest {
  rowVersion: number;
}

export interface AuditFirmSearchRequest {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  firmName?: string;
  firmRegistrationNumber?: string;
  statusCode?: string;
  includeInactive?: boolean;
}

// ---------------------------------------------------------------------------
// Audit Partner
// ---------------------------------------------------------------------------

export interface AuditPartner {
  id: Uuid;
  firmId: ID;
  firmName?: string | null;
  firmRegistrationNumber?: string | null;
  partnerName: string;
  membershipNumber: string;
  designation: string;
  signatureBlockText?: string | null;
  signatureImagePath?: string | null;
  statusId: ID;
  statusCode: string;
  statusName: string;
  statusColor?: string | null;
  rowVersion: number;
  createdAt: string;
  createdBy?: ID | null;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedBy?: ID | null;
  updatedByName?: string | null;
}

export interface CreateAuditPartnerRequest {
  firmId: ID;
  partnerName: string;
  membershipNumber: string;
  designation: string;
  signatureBlockText?: string | null;
  signatureImagePath?: string | null;
  statusCode?: string;
}

export interface UpdateAuditPartnerRequest extends CreateAuditPartnerRequest {
  rowVersion: number;
}

export interface AuditPartnerSearchRequest {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  firmId?: ID;
  partnerName?: string;
  membershipNumber?: string;
  statusCode?: string;
  includeInactive?: boolean;
}

// ---------------------------------------------------------------------------
// Director Signatory
// ---------------------------------------------------------------------------

export interface DirectorSignatory {
  id: Uuid;
  clientId: ID;
  clientLegalName?: string | null;
  clientCode?: string | null;
  signatoryName: string;
  din?: string | null;
  designation: string;
  signatureBlockText?: string | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  signatureImagePath?: string | null;
  statusId: ID;
  statusCode: string;
  statusName: string;
  statusColor?: string | null;
  rowVersion: number;
  createdAt: string;
  createdBy?: ID | null;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedBy?: ID | null;
  updatedByName?: string | null;
}

export interface CreateDirectorSignatoryRequest {
  clientId: ID;
  signatoryName: string;
  din?: string | null;
  designation: string;
  signatureBlockText?: string | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  signatureImagePath?: string | null;
  statusCode?: string;
}

export interface UpdateDirectorSignatoryRequest extends CreateDirectorSignatoryRequest {
  rowVersion: number;
}

export interface DirectorSignatorySearchRequest {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  clientId?: ID;
  signatoryName?: string;
  designation?: string;
  eligibleAsOf?: string;
  /** ADR-0024 — with activeTo: signatories whose effective window overlaps [activeFrom, activeTo] (ISO yyyy-MM-dd). */
  activeFrom?: string;
  activeTo?: string;
  statusCode?: string;
  includeInactive?: boolean;
}

// ---------------------------------------------------------------------------
// Engagement Signature Panel
// ---------------------------------------------------------------------------

/**
 * Which SIDE of the signature block a row prints on — not which master it
 * came from. A partnership firm's partners print on the entity side and so
 * share `DIRECTOR` with company directors; `SignatoryMasterType` separates
 * them.
 */
export type SignatoryRole = 'AUDITOR_PARTNER' | 'DIRECTOR' | 'OTHER';

/**
 * Which master `signatoryMasterId` points at: `AUDIT_PARTNER`
 * (fsa.tbl_audit_partner — the AUDIT FIRM's signing partners),
 * `DIRECTOR` (fsa.tbl_director_signatory) or `PARTNER` (fsa.tbl_partner —
 * the CLIENT's own partners, from FSA → Partners). Null/absent for a
 * free-text signatory, and on rows written before the column existed —
 * the API falls back to inferring it from role in that case.
 */
export type SignatoryMasterType = 'AUDIT_PARTNER' | 'DIRECTOR' | 'PARTNER';

export interface EngagementSignaturePanelSignatory {
  id: Uuid;
  panelId: ID;
  role: SignatoryRole;
  signatoryMasterId?: ID | null;
  signatoryMasterType?: SignatoryMasterType | null;
  columnPosition?: number | null;
  displayOrder: number;
  udinNumber?: string | null;
  nameSnapshot: string;
  designationSnapshot: string;
  dinOrMembershipSnapshot?: string | null;
  signatureBlockTextSnapshot?: string | null;
  signatureImagePathSnapshot?: string | null;
  rowVersion: number;
  createdAt: string;
  updatedAt?: string | null;
}

export interface EngagementSignaturePanel {
  id: Uuid;
  primaEngagementId: ID;
  engagementCode?: string | null;
  clientLegalName?: string | null;
  auditFirmId: ID;
  auditFirmName?: string | null;
  auditFirmRegistrationNumber?: string | null;
  auditFirmReportAttestationText?: string | null;
  /** Where the AUDITOR signs. */
  place?: string | null;
  /** Date the AUDITOR signs. */
  reportDate?: string | null;
  /** Where the directors / partners / proprietor sign — null prints no Place line on that side. */
  entityPlace?: string | null;
  /** Date the entity side signs — null means the auditor's report date. */
  entityDate?: string | null;
  notes?: string | null;
  signatories: EngagementSignaturePanelSignatory[];
  statusId: ID;
  statusCode: string;
  statusName: string;
  statusColor?: string | null;
  rowVersion: number;
  createdAt: string;
  updatedAt?: string | null;
}

export interface UpsertEngagementSignaturePanelSignatoryItem {
  role: SignatoryRole;
  signatoryMasterId?: ID | null;
  /** Required to select a PARTNER; otherwise the API infers it from `role`. */
  signatoryMasterType?: SignatoryMasterType | null;
  columnPosition?: number | null;
  displayOrder: number;
  /** 18-character ICAI UDIN (per partner per engagement). */
  udinNumber?: string | null;
  /** Free-text overrides for OTHER-role signatories. Ignored when signatoryMasterId is set. */
  nameOverride?: string | null;
  designationOverride?: string | null;
  dinOrMembershipOverride?: string | null;
  signatureBlockTextOverride?: string | null;
}

export interface UpsertEngagementSignaturePanelRequest {
  primaEngagementId: ID;
  auditFirmId: ID;
  place?: string | null;
  reportDate?: string | null;
  entityPlace?: string | null;
  entityDate?: string | null;
  notes?: string | null;
  signatories: UpsertEngagementSignaturePanelSignatoryItem[];
  rowVersion: number;
  statusCode?: string;
  /** Re-copy names / designations / DINs from the masters (default: keep the saved ones). */
  refreshFromMasters?: boolean;
}

/**
 * ICAI UDIN — 18 characters, structured as:
 *   · 2 digits  — last two digits of the year of generation  (26)
 *   · 6 digits  — the member's ICAI membership number         (999999)
 *   · 10 chars  — alphanumeric, randomly generated by ICAI    (XXXXXX1234)
 *
 * The random tail is alphanumeric, NOT alphabetic — it routinely contains
 * digits, so it must not be split into fixed alpha/digit runs.
 */
export const UDIN_REGEX = /^\d{8}[0-9A-Z]{10}$/;

/**
 * Synthetic UDIN used for the input placeholder and the validation message.
 * Deliberately NOT a real UDIN: membership 999999 is not issued and the
 * XXXXXX tail cannot occur, so it can never collide with a genuine
 * document identifier if it is ever copied out of the UI.
 */
export const UDIN_EXAMPLE = '26999999XXXXXX1234';

/** Message shown when {@link UDIN_REGEX} rejects an entry. */
export const UDIN_FORMAT_MESSAGE =
  `UDIN must be 18 characters: 2-digit year + 6-digit membership number + 10 alphanumeric (e.g. ${UDIN_EXAMPLE})`;
